import { useCallback, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import PageMeta from '../../../shared/PageMeta';
import { useAuth } from '../../../context/AuthContext';
import { formatNumber } from '../dashboard/utils/formatNumber';
import { deltaPhrase } from '../dashboard/utils/scopeWindow';
import { useAdminScope } from './hooks/useAdminScope';
import { useAdminAnalytics } from './hooks/useAdminAnalytics';
import {
  activationFunnel,
  activeSeries,
  adoptionSummary,
  breakdownByLevel,
  cycleSeries,
  deriveInsights,
  flowTotals,
  growthSeries,
  retentionCohorts,
  scopeData,
  taskFlowSeries,
  usageMomentum,
  type AdminDrill,
  type AdminInsight,
  type EngineerMomentum,
} from './utils/adminMetrics';
import type { AdminReportPayload } from './utils/adminExport';
import AdminBrief from './components/AdminBrief';
import AdminScopeBar from './components/AdminScopeBar';
import KpiStrip, { type AdminKpi } from './components/KpiStrip';
import GrowthPanel from './components/GrowthPanel';
import AdoptionPanel from './components/AdoptionPanel';
import UsageMomentumRoster from './components/UsageMomentumRoster';
import PulseHeatmap from './components/PulseHeatmap';
import TaskFlowPanel from './components/TaskFlowPanel';
import AdminDrilldownExplorer from './components/AdminDrilldownExplorer';
import AdminInsightsFeed from './components/AdminInsightsFeed';
import EngineerUsageDrawer from './components/EngineerUsageDrawer';

/**
 * Admin Analytics — the platform-health surface. Answers, in order:
 * is the portfolio growing (Growth), is the mobile app being used
 * (Adoption + Usage momentum), when do engineers work (Pulse), and is
 * task flow draining or drowning (Task flow) — all drillable
 * State → Location → Project → Engineer via the URL-backed scope.
 */
export default function AdminAnalyticsPage() {
  const { user } = useAuth();
  const [scope, actions] = useAdminScope();
  const data = useAdminAnalytics(scope);
  const [openEngineerId, setOpenEngineerId] = useState<string | null>(null);

  const phrase = deltaPhrase(scope.preset === 'today' ? 'custom' : scope.preset);
  const drill: AdminDrill = useMemo(
    () => ({
      state: scope.state,
      locationId: scope.locationId,
      projectId: scope.projectId,
      engineerId: scope.engineerId,
    }),
    [scope.state, scope.locationId, scope.projectId, scope.engineerId]
  );

  const computed = useMemo(() => {
    if (!data.current) return null;
    const current = data.current;
    const previous = data.previous;
    const scoped = scopeData(current, drill);
    const prevScoped = previous ? scopeData(previous, drill) : null;

    const flowPoints = taskFlowSeries(scoped.flow, scope.window, scope.granularity);
    const prevFlowPoints = prevScoped ? taskFlowSeries(prevScoped.flow, scope.prevWindow, scope.granularity) : [];
    const totals = flowTotals(flowPoints);
    const prevTotals = flowTotals(prevFlowPoints);

    const adoptionPoints = activeSeries(scoped.activity, scope.window, scope.granularity);
    const prevAdoptionPoints = prevScoped
      ? activeSeries(prevScoped.activity, scope.prevWindow, scope.granularity)
      : [];
    const adoption = adoptionSummary(scoped.activity, scope.window);
    const prevAdoption = adoptionSummary(prevScoped?.activity ?? [], scope.prevWindow);

    const growth = growthSeries(current, scope.window, scope.granularity, drill);
    const prevGrowth = previous ? growthSeries(previous, scope.prevWindow, scope.granularity, drill) : [];

    const momentum = usageMomentum(current, scoped, scope.window, scope.level === 1);
    const cycle = cycleSeries(
      current.cycleSamples.filter((s) => scoped.projectIds.has(s.projectId)),
      scope.window,
      scope.granularity
    );

    // Peak matrix per scope: portfolio-wide uses the state facet source;
    // any drill sums the per-engineer cells of engineers active in scope.
    const peakCells =
      scope.level === 1
        ? current.peakByState
        : current.peakByEngineer.filter((c) =>
            scope.engineerId ? c.engineerId === scope.engineerId : scoped.engineerIds.has(c.engineerId)
          );

    const breakdown = breakdownByLevel(current, previous, drill, scope.level);
    const insights = previous
      ? deriveInsights({
          current: { payload: current, window: scope.window },
          previous: { payload: previous, window: scope.prevWindow },
        })
      : [];

    return {
      scoped,
      flowPoints,
      prevFlowPoints,
      totals,
      prevTotals,
      adoptionPoints,
      prevAdoptionPoints,
      adoption,
      prevAdoption,
      growth,
      prevGrowth,
      momentum,
      cycle,
      peakCells,
      breakdown,
      insights,
      funnel: activationFunnel(current, scope.window),
      cohorts: retentionCohorts(current, scope.window),
    };
  }, [data.current, data.previous, drill, scope]);

  const kpis: AdminKpi[] = useMemo(() => {
    if (!computed) return [];
    const growthNow = computed.growth.reduce((s, p) => s + p.newProjects, 0);
    const growthPrev = computed.prevGrowth.reduce((s, p) => s + p.newProjects, 0);
    const int = (n: number) => formatNumber(Math.round(n));
    return [
      {
        key: 'new-projects',
        label: 'New projects',
        value: growthNow,
        format: int,
        delta: computed.prevGrowth.length > 0 ? growthNow - growthPrev : null,
        deltaSuffix: '',
        definition: 'Active projects whose record was created inside the window',
        spark: computed.growth.map((p) => p.newProjects),
      },
      {
        key: 'wau',
        label: 'Active engineers (WAU)',
        value: computed.adoption.wau,
        format: int,
        delta: computed.adoption.wau - computed.prevAdoption.wau,
        deltaSuffix: '',
        definition: 'Unique engineers with ≥1 app action in the last 7 days of the window',
        spark: computed.adoptionPoints.map((p) => p.active),
      },
      {
        key: 'stickiness',
        label: 'Stickiness',
        value: Math.round((computed.adoption.stickiness ?? 0) * 100),
        format: (n) => `${Math.round(n)}%`,
        delta:
          computed.adoption.stickiness !== null && computed.prevAdoption.stickiness !== null
            ? Math.round((computed.adoption.stickiness - computed.prevAdoption.stickiness) * 100)
            : null,
        deltaSuffix: ' pts',
        definition: 'Average daily active engineers ÷ monthly active engineers',
        spark: computed.adoptionPoints.map((p) => p.active),
      },
      {
        key: 'created',
        label: 'Tasks created',
        value: computed.totals.created,
        format: int,
        delta: computed.prevFlowPoints.length > 0 ? computed.totals.created - computed.prevTotals.created : null,
        deltaSuffix: '',
        definition: 'Tasks whose record was created inside the window, in scope',
        spark: computed.flowPoints.map((p) => p.created),
      },
      {
        key: 'completion',
        label: 'Completion rate',
        value: computed.totals.completionPct ?? 0,
        format: (n) => `${Math.round(n)}%`,
        delta:
          computed.totals.completionPct !== null && computed.prevTotals.completionPct !== null
            ? computed.totals.completionPct - computed.prevTotals.completionPct
            : null,
        deltaSuffix: ' pts',
        definition: `Completed share of ${formatNumber(computed.totals.scheduled)} tasks scheduled in the window`,
        spark: computed.flowPoints.map((p) => p.completionPct ?? 0),
      },
      {
        key: 'skip',
        label: 'Skip rate',
        value: computed.totals.skipPct ?? 0,
        format: (n) => `${Math.round(n)}%`,
        delta:
          computed.totals.skipPct !== null && computed.prevTotals.skipPct !== null
            ? computed.totals.skipPct - computed.prevTotals.skipPct
            : null,
        deltaSuffix: ' pts',
        definition: `Skipped share of ${formatNumber(computed.totals.scheduled)} tasks scheduled in the window`,
        spark: computed.flowPoints.map((p) => p.skipPct ?? 0),
      },
    ];
  }, [computed]);

  const scopePath = useMemo(() => {
    const path = ['India'];
    if (scope.state) path.push(scope.state);
    if (scope.locationId) {
      const sample = data.current?.projects.find((p) => (p.locationId ?? 'unmapped') === scope.locationId);
      path.push(sample?.locationName ?? 'Location');
    }
    if (scope.projectId) {
      path.push(data.current?.projects.find((p) => p.projectId === scope.projectId)?.name ?? 'Project');
    }
    if (scope.engineerId) {
      path.push(data.current?.engineers.find((e) => e.engineerId === scope.engineerId)?.name ?? 'Engineer');
    }
    return path;
  }, [scope, data.current]);

  const briefSentence = useMemo(() => {
    if (!computed) return '';
    const growthNow = computed.growth.reduce((s, p) => s + p.newProjects, 0);
    const wauDelta = computed.adoption.wau - computed.prevAdoption.wau;
    const parts = [
      growthNow > 0
        ? `${formatNumber(growthNow)} project${growthNow === 1 ? '' : 's'} added`
        : 'no new projects',
      `WAU ${wauDelta > 0 ? 'up' : wauDelta < 0 ? 'down' : 'level at'} ${wauDelta === 0 ? computed.adoption.wau : Math.abs(wauDelta)}`,
    ];
    const anomaly = computed.insights[0];
    const mood = anomaly?.severity === 'critical' ? 'Mixed window' : 'Steady window';
    return `${mood} — ${parts.join(' and ')}${anomaly ? `; ${anomaly.sentence.replace(/\.$/, '').toLowerCase()} — worth a look` : ''}.`;
  }, [computed]);

  const openInsight = useCallback(
    (insight: AdminInsight) => {
      actions.setSelection({
        state: insight.link.state ?? null,
        locationId: insight.link.site ?? null,
        projectId: insight.link.project ?? null,
        engineerId: insight.link.engineer ?? null,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [actions]
  );

  const getReport = useCallback((): AdminReportPayload | null => {
    if (!computed) return null;
    return {
      scopePath,
      window: scope.window,
      granularity: scope.granularity,
      kpis: kpis.map((k) => ({
        label: k.label,
        value: k.format(k.value),
        delta: k.delta === null ? 'no prior data' : `${k.delta > 0 ? '+' : ''}${k.delta}${k.deltaSuffix} ${phrase}`,
      })),
      totals: computed.totals,
      breakdownLevel: computed.breakdown.label.replace(/s$/, ''),
      breakdown: computed.breakdown.entries.map((e) => ({
        name: e.name,
        created: e.created,
        scheduled: e.scheduled,
        completed: e.completed,
        skipped: e.skipped,
        pending: e.pending,
        completionPct: e.completionPct,
        skipPct: e.skipPct,
        activeEngineers: e.activeEngineers,
      })),
      momentum: computed.momentum,
      insights: computed.insights.map((i) => i.sentence),
    };
  }, [computed, scopePath, scope.window, scope.granularity, kpis, phrase]);

  const openEngineer: EngineerMomentum | null = useMemo(() => {
    if (!openEngineerId || !computed) return null;
    return computed.momentum.find((m) => m.engineerId === openEngineerId) ?? null;
  }, [openEngineerId, computed]);

  const crumbs = useMemo(() => {
    const list: Array<{ label: string; level: 1 | 2 | 3 | 4 | 'current' }> = [
      { label: 'India', level: scope.level === 1 ? 'current' : 1 },
    ];
    if (scope.state) list.push({ label: scope.state, level: scope.level === 2 ? 'current' : 2 });
    if (scope.locationId) list.push({ label: scopePath[2] ?? 'Location', level: scope.level === 3 ? 'current' : 3 });
    if (scope.projectId) list.push({ label: scopePath[3] ?? 'Project', level: scope.level === 4 ? 'current' : 4 });
    if (scope.engineerId) list.push({ label: scopePath[scopePath.length - 1] ?? 'Engineer', level: 'current' });
    return list;
  }, [scope, scopePath]);

  const userName = (user?.name as string | undefined) ?? '';
  const scopeCaption =
    scope.level === 1
      ? 'all states'
      : `${scopePath[scopePath.length - 1]}${scope.level > 1 && !scope.engineerId ? ' · engineers active in scope' : ''}`;

  return (
    <>
      <PageMeta
        title="Admin Analytics - Sorigin FMS-OMS"
        description="Platform growth, mobile adoption, peak activity and task flow analytics"
      />
      <div className="min-h-screen space-y-4 pb-24">
        <AdminBrief
          sentence={briefSentence}
          topInsight={computed?.insights[0] ?? null}
          onOpenInsight={openInsight}
          userName={userName}
          loading={data.isLoading}
        />

        {data.isError && (
          <div className="flex items-center gap-3 rounded-xl border border-[#9F1239]/30 bg-[#9F1239]/5 px-4 py-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-[#9F1239]" />
            <div className="text-sm">
              <p className="font-semibold text-[#9F1239]">Analytics failed to load</p>
              <p className="text-[#9F1239]/80">
                {data.error instanceof Error ? data.error.message : 'Unknown error'} — refresh to retry.
              </p>
            </div>
          </div>
        )}

        <AdminScopeBar
          scope={scope}
          actions={actions}
          payload={data.current}
          refreshing={data.isFetching}
          getReport={getReport}
        />

        <KpiStrip kpis={kpis} phrase={phrase} loading={data.isLoading} />

        <div className="grid gap-4 xl:grid-cols-2">
          <GrowthPanel
            points={computed?.growth ?? []}
            prevPoints={computed?.prevGrowth ?? []}
            compare={scope.compare}
            phrase={phrase}
            loading={data.isLoading}
          />
          <AdoptionPanel
            points={computed?.adoptionPoints ?? []}
            prevPoints={computed?.prevAdoptionPoints ?? []}
            summary={computed?.adoption ?? { wau: 0, mau: 0, avgDau: 0, stickiness: null }}
            prevSummary={computed?.prevAdoption ?? { wau: 0, mau: 0, avgDau: 0, stickiness: null }}
            funnel={computed?.funnel ?? []}
            cohorts={computed?.cohorts ?? []}
            granularity={scope.granularity}
            compare={scope.compare}
            phrase={phrase}
            loading={data.isLoading}
          />
        </div>

        <UsageMomentumRoster
          rows={computed?.momentum ?? []}
          phrase={phrase}
          loading={data.isLoading}
          onOpen={(engineer) => setOpenEngineerId(engineer.engineerId)}
        />

        <PulseHeatmap
          cells={computed?.peakCells ?? []}
          byState={data.current?.peakByState ?? []}
          scopeCaption={scopeCaption}
          loading={data.isLoading}
        />

        <TaskFlowPanel
          points={computed?.flowPoints ?? []}
          prevPoints={computed?.prevFlowPoints ?? []}
          totals={
            computed?.totals ?? {
              created: 0,
              scheduled: 0,
              scheduledCompleted: 0,
              scheduledSkipped: 0,
              scheduledPending: 0,
              resolved: 0,
              netFlow: 0,
              completionPct: null,
              skipPct: null,
              pendingPct: null,
            }
          }
          cycle={computed?.cycle ?? []}
          compare={scope.compare}
          phrase={phrase}
          loading={data.isLoading}
        />

        <AdminDrilldownExplorer
          scope={scope}
          actions={actions}
          levelLabel={computed?.breakdown.label ?? 'States'}
          entries={computed?.breakdown.entries ?? []}
          crumbs={crumbs}
          phrase={phrase}
          loading={data.isLoading}
          onOpenEngineer={setOpenEngineerId}
        />

        <AdminInsightsFeed insights={computed?.insights ?? []} loading={data.isLoading} onOpen={openInsight} />

        <p className="px-2 text-[10px] font-medium" style={{ color: '#7C8A82' }}>
          "App action" = a task status update by an engineer — the closest tracked proxy for mobile-app usage.
          Onboarding = account creation. Sessions, logins and skip reasons aren't instrumented yet — see
          docs/admin-analytics-instrumentation.md for the full event list.
        </p>
      </div>

      <EngineerUsageDrawer
        engineer={openEngineer}
        payload={data.current}
        window={scope.window}
        phrase={phrase}
        onClose={() => setOpenEngineerId(null)}
      />
    </>
  );
}
